<template>
    <div v-if="visible" class="orarioweb-backdrop" @click.self="close">
        <section class="orarioweb-wizard" role="dialog" aria-modal="true" aria-labelledby="orarioweb-title">
            <header class="orarioweb-header">
                <div>
                    <span class="orarioweb-kicker">Importazione assistita</span>
                    <h3 id="orarioweb-title">Importa calendario da OrarioWeb</h3>
                    <p>{{ courseLabel }}</p>
                </div>
                <div class="orarioweb-header-actions">
                    <button type="button" class="orarioweb-guide-button" :disabled="loading" @click="guideVisible = !guideVisible">
                        <i class="voyager-info-circled"></i> {{ guideVisible ? 'Torna al wizard' : 'Guida' }}
                    </button>
                    <button type="button" class="orarioweb-close" :disabled="loading" @click="close">&times;</button>
                </div>
            </header>

            <nav v-if="!guideVisible" class="orarioweb-steps" aria-label="Passaggi importazione">
                <button v-for="item in stepItems" :key="item.number" type="button"
                        :class="{active: step === item.number, done: step > item.number}"
                        :disabled="item.number > maxReachableStep || loading" @click="goToStep(item.number)">
                    <span>{{ item.number }}</span>{{ item.label }}
                </button>
            </nav>

            <div v-if="error" class="alert alert-danger orarioweb-error">{{ error }}</div>
            <div v-if="loading" class="orarioweb-loading"><i class="voyager-refresh"></i> {{ loadingMessage }}</div>

            <main class="orarioweb-body">
                <section v-if="guideVisible" class="orarioweb-step orarioweb-guide">
                    <div class="orarioweb-title-row">
                        <div>
                            <h4>Guida all’importazione da OrarioWeb</h4>
                            <p>Il wizard trasforma l’orario settimanale esportato da OrarioWeb nel calendario del corso aperto.</p>
                        </div>
                        <span class="label label-info">Il calendario cambia solo all’ultimo passaggio</span>
                    </div>

                    <div class="orarioweb-guide-grid">
                        <article>
                            <strong>1. File</strong>
                            <p>Esporta da OrarioWeb un file XLSX o XLS. Può contenere più classi: il sistema lo analizza senza modificare il calendario.</p>
                        </article>
                        <article>
                            <strong>2. Analisi</strong>
                            <p>Scegli la classe da importare, controlla il lunedì della settimana e verifica le fasce. Ogni ora deve durare 60 minuti.</p>
                        </article>
                        <article>
                            <strong>3. Associazioni</strong>
                            <p>Abbina i nomi dei docenti alle risorse umane e le materie ai moduli. Il tutor è facoltativo e deve essere già assegnato al corso.</p>
                        </article>
                        <article>
                            <strong>4. Anteprima</strong>
                            <p>Scegli se integrare o sostituire. L’anteprima mostra lezioni riconosciute, conflitti, disponibilità e altri blocchi prima di qualsiasi scrittura.</p>
                        </article>
                        <article>
                            <strong>5. Importazione</strong>
                            <p>Conferma soltanto dopo il controllo. Il calendario viene applicato e Calendar V2 si ricarica automaticamente.</p>
                        </article>
                    </div>

                    <h5>Integra o sostituisci?</h5>
                    <div class="orarioweb-guide-modes">
                        <div>
                            <strong>Integra</strong>
                            <p>Mantiene le lezioni esistenti. Le righe identiche sono riconosciute; sovrapposizioni e conflitti devono essere risolti.</p>
                        </div>
                        <div class="danger">
                            <strong>Sostituisci</strong>
                            <p>Elimina tutte le lezioni del corso nell’intera settimana del file, anche nei giorni rimasti vuoti, e ricrea il calendario. Richiede due conferme.</p>
                        </div>
                    </div>

                    <h5>Associazioni ricordate e controlli</h5>
                    <ul>
                        <li>Le associazioni marcate <strong>Ricorda</strong> vengono salvate solo dopo un’importazione riuscita.</li>
                        <li>I docenti vengono ricordati per ente e sede; i moduli per singolo corso.</li>
                        <li>Un docente non ancora assegnato al modulo viene associato durante l’importazione.</li>
                        <li>Il sistema controlla ore, disponibilità, conflitti tra risorse, corsi articolati, presenze e blocchi DDR.</li>
                        <li>L’aula resta un’informazione del calendario, ma non genera un conflitto bloccante.</li>
                        <li>Se chiudi il wizard, la bozza resta disponibile e viene riproposta alla riapertura.</li>
                    </ul>

                    <div class="alert alert-warning">
                        Prima di usare <strong>Sostituisci</strong>, controlla sempre corso, classe, settimana e anteprima: l’operazione rimuove anche eventuali lezioni non presenti nel file.
                    </div>
                    <div class="orarioweb-actions">
                        <button type="button" class="btn btn-primary" @click="guideVisible = false">Torna al wizard</button>
                    </div>
                </section>

                <section v-else-if="step === 1" class="orarioweb-step">
                    <h4>1. Carica il file Excel</h4>
                    <p>Esporta l’orario settimanale da OrarioWeb in formato XLSX o XLS. Il file viene analizzato senza modificare il calendario.</p>
                    <div class="alert alert-info">
                        Il file verrà importato nel corso <strong>{{ courseLabel }}</strong>. Per importare un altro corso, apri il suo Calendar V2.
                    </div>
                    <label class="orarioweb-dropzone">
                        <i class="voyager-upload"></i>
                        <strong>{{ selectedFile ? selectedFile.name : 'Scegli il file OrarioWeb' }}</strong>
                        <span>XLSX o XLS, massimo 20 MB</span>
                        <input ref="fileInput" type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" @change="selectFile">
                    </label>
                    <div class="orarioweb-actions">
                        <button type="button" class="btn btn-primary" :disabled="!selectedFile || loading" @click="analyse">
                            Analizza il file
                        </button>
                    </div>
                </section>

                <section v-else-if="step === 2" class="orarioweb-step">
                    <div class="orarioweb-title-row">
                        <div><h4>2. Controlla la prima analisi</h4><p>Il sistema ha riconosciuto struttura, classi e fasce del file <strong>{{ draft.filename }}</strong>.</p></div>
                        <button type="button" class="btn btn-xs btn-default" @click="discardDraft">Carica un altro file</button>
                    </div>
                    <div class="orarioweb-analysis-grid">
                        <div class="form-group">
                            <label>Classe OrarioWeb da importare</label>
                            <select class="form-control" v-model="settings.selected_section_key" @change="sectionChanged">
                                <option :value="null">Seleziona la classe</option>
                                <option v-for="section in draft.sections" :key="section.key" :value="section.key">
                                    {{ section.label }} · {{ section.lesson_count }} lezioni
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Primo giorno della settimana</label>
                            <input class="form-control" type="date" v-model="settings.week_start">
                            <small>Il nome del file viene usato solo come proposta: verifica sempre la data.</small>
                        </div>
                    </div>
                    <h5>Corrispondenza delle ore</h5>
                    <p class="text-muted">La granularità è sempre di 60 minuti. Correggi le fasce se l’orario della sede è diverso.</p>
                    <div class="orarioweb-slot-grid">
                        <div v-for="slot in settings.slots" :key="slot.hour_index" class="orarioweb-slot">
                            <strong>{{ slot.label }}</strong>
                            <input class="form-control" type="time" step="300" v-model="slot.start">
                            <span>–</span>
                            <input class="form-control" type="time" step="300" v-model="slot.end">
                        </div>
                    </div>
                    <div class="orarioweb-actions">
                        <button type="button" class="btn btn-default" @click="step = 1">Indietro</button>
                        <button type="button" class="btn btn-primary" :disabled="!settings.selected_section_key || !settings.week_start" @click="confirmAnalysis">
                            Continua con le associazioni
                        </button>
                    </div>
                </section>

                <section v-else-if="step === 3" class="orarioweb-step">
                    <h4>3. Conferma docenti, materie e tutor</h4>
                    <p>Le associazioni ricordate sono già proposte. Controllale: diventeranno definitive soltanto dopo l’importazione riuscita.</p>
                    <h5>Docenti</h5>
                    <div class="table-responsive">
                        <table class="table table-condensed orarioweb-map-table">
                            <thead><tr><th>Nel file</th><th>Lezioni</th><th>Risorsa umana in Manager</th><th>Memoria</th></tr></thead>
                            <tbody>
                                <tr v-for="row in teacherRows" :key="row.key" :class="{'danger': !row.target_id}">
                                    <td><strong>{{ row.label }}</strong><small v-if="row.suggestion_kind">{{ suggestionLabel(row.suggestion_kind) }}</small></td>
                                    <td>{{ row.lesson_count }}</td>
                                    <td>
                                        <select class="form-control" v-model.number="row.target_id">
                                            <option :value="null">Seleziona il docente</option>
                                            <option v-for="teacher in draft.options.teachers" :key="teacher.id" :value="teacher.id">
                                                {{ teacher.label }}{{ teacher.assigned_to_course ? '' : ' · da associare al modulo' }}
                                            </option>
                                        </select>
                                    </td>
                                    <td><label><input type="checkbox" v-model="row.remember"> Ricorda</label></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <h5>Materie e moduli</h5>
                    <div class="table-responsive">
                        <table class="table table-condensed orarioweb-map-table">
                            <thead><tr><th>Materia nel file</th><th>Lezioni</th><th>Modulo del corso</th><th>Memoria</th></tr></thead>
                            <tbody>
                                <tr v-for="row in moduleRows" :key="row.key" :class="{'danger': !row.target_id}">
                                    <td><strong>{{ row.label }}</strong><small v-if="row.suggestion_kind">{{ suggestionLabel(row.suggestion_kind) }}</small></td>
                                    <td>{{ row.lesson_count }}</td>
                                    <td>
                                        <select class="form-control" v-model.number="row.target_id">
                                            <option :value="null">Seleziona il modulo</option>
                                            <option v-for="module in draft.options.modules" :key="module.id" :value="module.id">{{ module.label }}</option>
                                        </select>
                                    </td>
                                    <td><label><input type="checkbox" v-model="row.remember"> Ricorda</label></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="form-group orarioweb-tutor">
                        <label>Tutor da inserire nelle lezioni (facoltativo)</label>
                        <select class="form-control" v-model.number="defaultTutorId">
                            <option :value="null">Nessun tutor</option>
                            <option v-for="tutor in draft.options.tutors" :key="tutor.id" :value="tutor.id">{{ tutor.label }}</option>
                        </select>
                        <small>Sono disponibili soltanto i tutor già assegnati al corso.</small>
                    </div>
                    <div class="alert alert-info">
                        Se scegli un docente non ancora assegnato al corso, al momento dell’importazione verrà associato ai moduli corrispondenti per le ore presenti nel file.
                    </div>
                    <div class="orarioweb-actions">
                        <button type="button" class="btn btn-default" @click="step = 2">Indietro</button>
                        <button type="button" class="btn btn-primary" :disabled="!associationsComplete" @click="confirmAssociations">
                            Conferma associazioni
                        </button>
                    </div>
                </section>

                <section v-else-if="step === 4" class="orarioweb-step">
                    <h4>4. Controlla e conferma l’importazione</h4>
                    <div class="orarioweb-mode-grid">
                        <label :class="{selected: settings.replace_mode === 'integrate'}">
                            <input type="radio" value="integrate" v-model="settings.replace_mode" @change="preview = null">
                            <strong>Integra il calendario</strong>
                            <span>Mantiene le lezioni esistenti. Gli eventuali conflitti vengono segnalati.</span>
                        </label>
                        <label class="replace" :class="{selected: settings.replace_mode === 'replace', disabled: !draft.can_replace}">
                            <input type="radio" value="replace" v-model="settings.replace_mode" :disabled="!draft.can_replace" @change="preview = null">
                            <strong>Sostituisci l’intera settimana</strong>
                            <span>Elimina tutte le lezioni del corso dal lunedì alla domenica, anche nei giorni vuoti, e le ricrea dal file. Presenze e DDR restano bloccanti.</span>
                        </label>
                    </div>
                    <button type="button" class="btn btn-info" :disabled="!settings.replace_mode || loading" @click="generatePreview">
                        Genera anteprima finale
                    </button>

                    <div v-if="preview" class="orarioweb-preview">
                        <div class="orarioweb-summary">
                            <span><strong>{{ preview.summary.total }}</strong> righe</span>
                            <span class="ok"><strong>{{ preview.summary.insert }}</strong> da inserire</span>
                            <span><strong>{{ preview.summary.unchanged }}</strong> già presenti</span>
                            <span class="warning"><strong>{{ preview.summary.replace_day }}</strong> da sostituire</span>
                            <span v-if="settings.replace_mode === 'replace'" class="warning">
                                <strong>{{ Object.keys(preview.day_replacements || {}).length }}</strong> giornate coinvolte
                            </span>
                            <span class="error"><strong>{{ Number(preview.summary.errors || 0) + Number(preview.summary.day_errors || 0) }}</strong> errori</span>
                        </div>
                        <div v-if="Number(preview.summary.errors || 0) + Number(preview.summary.day_errors || 0)" class="alert alert-danger">
                            L’importazione è bloccata finché l’anteprima contiene errori.
                        </div>
                        <div class="orarioweb-preview-list">
                            <div v-for="row in preview.rows" :key="row.row" :class="['orarioweb-preview-row', row.status]">
                                <span>{{ row.date }} · {{ row.start }}–{{ row.end }}</span>
                                <strong>{{ row.module }}</strong>
                                <span>{{ row.teacher }}</span>
                                <small v-for="message in row.errors" :key="message">{{ message }}</small>
                                <a v-for="link in (row.errors.length ? (row.teacher_schedule_links || []) : [])" :key="link.url" :href="link.url" target="_blank" rel="noopener">{{ link.label }}</a>
                                <small v-for="warning in row.warnings" :key="warning.message || warning">{{ warning.message || warning }}</small>
                            </div>
                        </div>

                        <div v-if="settings.replace_mode === 'replace'" class="alert alert-danger orarioweb-confirmations">
                            <strong>Attenzione: operazione distruttiva</strong>
                            <p>Tutte le lezioni del corso comprese nell’intera settimana saranno eliminate, incluse quelle nei giorni rimasti vuoti nel file.</p>
                            <label><input type="checkbox" v-model="replacementConfirmed"> Ho verificato corso, classe, settimana e anteprima.</label>
                            <label><input type="checkbox" v-model="destructiveConfirmed"> Confermo di voler eliminare e sostituire le lezioni esistenti nell’intera settimana.</label>
                        </div>
                    </div>
                    <div class="orarioweb-actions">
                        <button type="button" class="btn btn-default" @click="step = 3">Indietro</button>
                        <button type="button" class="btn btn-success" :disabled="!canApply" @click="applyImport">
                            Importa calendario
                        </button>
                    </div>
                </section>

                <section v-else class="orarioweb-step orarioweb-result">
                    <i class="voyager-check"></i>
                    <h4>5. Importazione completata</h4>
                    <p>Il calendario è stato aggiornato e Calendar V2 è stato ricaricato.</p>
                    <div v-if="report" class="orarioweb-summary">
                        <span><strong>{{ report.summary.inserted }}</strong> inserite</span>
                        <span><strong>{{ report.summary.replaced }}</strong> sostituite</span>
                        <span><strong>{{ report.summary.unchanged }}</strong> già allineate</span>
                        <span><strong>{{ report.summary.skipped }}</strong> saltate</span>
                    </div>
                    <div class="orarioweb-actions">
                        <button type="button" class="btn btn-primary" @click="close">Chiudi e torna al calendario</button>
                    </div>
                </section>
            </main>
        </section>
    </div>
</template>

<script>
import api from './calendarApi.js'

export default {
    props: {
        visible: {type: Boolean, default: false},
        config: {type: Object, required: true},
        course: {type: Object, default: () => ({})}
    },
    data() {
        return {
            step: 1,
            maxReachableStep: 1,
            loading: false,
            loadingMessage: '',
            error: '',
            selectedFile: null,
            draft: null,
            settings: {selected_section_key: null, week_start: null, slots: [], default_tutor_id: null, replace_mode: null, remember_settings: true},
            teacherRows: [],
            moduleRows: [],
            defaultTutorId: null,
            preview: null,
            report: null,
            replacementConfirmed: false,
            destructiveConfirmed: false,
            guideVisible: false
        }
    },
    computed: {
        stepItems() {
            return [
                {number: 1, label: 'File'}, {number: 2, label: 'Analisi'}, {number: 3, label: 'Associazioni'},
                {number: 4, label: 'Conferma'}, {number: 5, label: 'Importazione'}
            ]
        },
        courseLabel() {
            return (this.draft && this.draft.course && this.draft.course.label) || this.course.label || this.course.titolo || 'Corso selezionato'
        },
        associationsComplete() {
            return this.teacherRows.length > 0 && this.moduleRows.length > 0 &&
                this.teacherRows.every((row) => !!row.target_id) && this.moduleRows.every((row) => !!row.target_id)
        },
        canApply() {
            if (!this.preview || Number(this.preview.summary.errors || 0) + Number(this.preview.summary.day_errors || 0) > 0 || this.loading) return false
            if (this.settings.replace_mode === 'replace') return this.replacementConfirmed && this.destructiveConfirmed
            return this.settings.replace_mode === 'integrate'
        }
    },
    watch: {
        visible(value) {
            if (value) this.open()
        }
    },
    mounted() {
        if (this.visible) this.open()
    },
    methods: {
        async open() {
            this.error = ''
            this.report = null
            this.guideVisible = false
            this.loading = true
            this.loadingMessage = 'Controllo delle bozze salvate…'
            try {
                const draft = await api.orarioWebLatest(this.config, this.config.corso_id)
                if (draft) {
                    this.hydrate(draft)
                    this.step = Math.min(4, Math.max(2, Number(draft.last_step || 2)))
                    this.maxReachableStep = this.step
                } else {
                    this.reset()
                }
            } catch (error) {
                this.error = this.message(error, 'Impossibile recuperare le bozze di importazione.')
            } finally {
                this.loading = false
            }
        },
        reset() {
            this.step = 1
            this.maxReachableStep = 1
            this.selectedFile = null
            this.draft = null
            this.preview = null
            this.report = null
            this.replacementConfirmed = false
            this.destructiveConfirmed = false
            this.guideVisible = false
        },
        close() {
            if (!this.loading) this.$emit('close')
        },
        goToStep(number) {
            if (number <= this.maxReachableStep) this.step = number
        },
        selectFile(event) {
            const file = event.target.files && event.target.files.length ? event.target.files[0] : null
            const extension = file && file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : ''
            if (file && !['xlsx', 'xls'].includes(extension)) {
                this.selectedFile = null
                this.error = 'Carica un file Excel in formato XLSX o XLS.'
                event.target.value = ''
                return
            }
            this.error = ''
            this.selectedFile = file
        },
        async analyse() {
            const form = new FormData()
            form.append('corso_id', this.config.corso_id)
            form.append('file', this.selectedFile)
            await this.run('Analisi del file OrarioWeb…', async () => {
                this.hydrate(await api.orarioWebAnalyse(this.config, form))
                this.step = 2
                this.maxReachableStep = 2
            })
        },
        hydrate(draft) {
            this.draft = draft
            this.settings = Object.assign({}, this.settings, JSON.parse(JSON.stringify(draft.settings || {})))
            this.settings.slots = JSON.parse(JSON.stringify((draft.settings && draft.settings.slots) || []))
            this.teacherRows = JSON.parse(JSON.stringify(draft.teachers || []))
            this.moduleRows = JSON.parse(JSON.stringify(draft.modules || []))
            this.defaultTutorId = (draft.settings && draft.settings.default_tutor_id) || null
            this.preview = draft.preview || null
        },
        async sectionChanged() {
            if (!this.draft) return
            await this.saveSettings('Aggiornamento della classe selezionata…')
        },
        async saveSettings(message) {
            const updated = await api.orarioWebUpdate(this.config, this.draft.uuid, {settings: this.settings})
            this.hydrate(updated)
            return updated
        },
        async confirmAnalysis() {
            await this.run('Salvataggio dell’analisi…', async () => {
                await this.saveSettings()
                this.step = 3
                this.maxReachableStep = Math.max(this.maxReachableStep, 3)
            })
        },
        associationPayload() {
            const teachers = {}
            this.teacherRows.forEach((row) => { teachers[row.key] = {source_label: row.label, target_id: row.target_id, remember: row.remember} })
            const modules = {}
            this.moduleRows.forEach((row) => { modules[row.key] = {source_label: row.label, target_id: row.target_id, remember: row.remember} })
            return {teachers, modules, default_tutor_id: this.defaultTutorId || null}
        },
        async confirmAssociations() {
            await this.run('Conferma delle associazioni…', async () => {
                this.settings.default_tutor_id = this.defaultTutorId || null
                const updated = await api.orarioWebUpdate(this.config, this.draft.uuid, {
                    settings: this.settings,
                    associations: this.associationPayload()
                })
                this.hydrate(updated)
                this.step = 4
                this.maxReachableStep = 4
            })
        },
        async generatePreview() {
            await this.run('Validazione del calendario e costruzione dell’anteprima…', async () => {
                const updated = await api.orarioWebUpdate(this.config, this.draft.uuid, {settings: this.settings})
                this.hydrate(updated)
                const response = await api.orarioWebPreview(this.config, this.draft.uuid)
                this.hydrate(response.import)
                this.preview = response.preview
                this.replacementConfirmed = false
                this.destructiveConfirmed = false
            })
        },
        async applyImport() {
            await this.run('Importazione del calendario in corso…', async () => {
                const response = await api.orarioWebApply(this.config, this.draft.uuid, {
                    replacement_confirmed: this.replacementConfirmed,
                    destructive_confirmed: this.destructiveConfirmed
                })
                this.report = response.report
                this.hydrate(response.import)
                this.step = 5
                this.maxReachableStep = 5
                this.$emit('imported', response.report)
            })
        },
        async discardDraft() {
            if (this.draft) {
                await this.run('Chiusura della bozza…', async () => {
                    await api.orarioWebDiscard(this.config, this.draft.uuid)
                    this.reset()
                })
            } else {
                this.reset()
            }
        },
        suggestionLabel(kind) {
            return kind === 'remembered' ? 'Associazione ricordata' : 'Corrispondenza proposta'
        },
        async run(message, callback) {
            this.loading = true
            this.loadingMessage = message || 'Operazione in corso…'
            this.error = ''
            try {
                await callback()
            } catch (error) {
                this.error = this.message(error, 'Operazione non completata.')
            } finally {
                this.loading = false
            }
        },
        message(error, fallback) {
            const response = error && error.response && error.response.data
            if (response && response.errors) {
                const key = Object.keys(response.errors)[0]
                if (key && response.errors[key] && response.errors[key][0]) return response.errors[key][0]
            }
            return (response && response.message) || fallback
        }
    }
}
</script>

<style scoped>
.orarioweb-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.65);z-index:12000;display:flex;align-items:flex-start;justify-content:center;padding:3vh 18px;overflow:auto}.orarioweb-wizard{width:min(1180px,100%);min-height:620px;background:#fff;border-radius:10px;box-shadow:0 24px 80px rgba(15,23,42,.35);overflow:hidden}.orarioweb-header{display:flex;justify-content:space-between;gap:20px;padding:22px 28px;background:#0f3d63;color:#fff}.orarioweb-header h3{margin:3px 0}.orarioweb-header p{margin:0;opacity:.85}.orarioweb-header-actions{display:flex;align-items:flex-start;gap:14px}.orarioweb-guide-button{border:1px solid rgba(255,255,255,.55);border-radius:5px;background:rgba(255,255,255,.1);color:#fff;padding:7px 11px}.orarioweb-guide-button:hover{background:rgba(255,255,255,.2)}.orarioweb-kicker{text-transform:uppercase;font-size:11px;letter-spacing:.1em;color:#7dd3fc}.orarioweb-close{border:0;background:transparent;color:#fff;font-size:32px;line-height:1}.orarioweb-steps{display:grid;grid-template-columns:repeat(5,1fr);border-bottom:1px solid #dbe3eb}.orarioweb-steps button{border:0;background:#f7fafc;padding:13px 8px;color:#64748b}.orarioweb-steps button span{display:inline-flex;width:24px;height:24px;border-radius:50%;align-items:center;justify-content:center;background:#dbe3eb;margin-right:6px}.orarioweb-steps button.active{background:#e0f2fe;color:#075985;font-weight:700}.orarioweb-steps button.done{color:#047857}.orarioweb-steps button.done span{background:#10b981;color:#fff}.orarioweb-body{padding:26px 30px}.orarioweb-step h4{margin-top:0}.orarioweb-guide-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin:22px 0}.orarioweb-guide-grid article{border:1px solid #dbe3eb;border-radius:7px;background:#f8fafc;padding:14px}.orarioweb-guide-grid article strong{color:#075985}.orarioweb-guide-grid article p,.orarioweb-guide-modes p{margin:7px 0 0}.orarioweb-guide-modes{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:22px}.orarioweb-guide-modes>div{border:2px solid #bae6fd;border-radius:7px;background:#f0f9ff;padding:14px}.orarioweb-guide-modes>div.danger{border-color:#fecaca;background:#fef2f2}.orarioweb-guide li{margin-bottom:7px}.orarioweb-dropzone{display:flex;min-height:220px;border:2px dashed #94a3b8;border-radius:10px;align-items:center;justify-content:center;flex-direction:column;gap:9px;background:#f8fafc;cursor:pointer}.orarioweb-dropzone i{font-size:42px;color:#0284c7}.orarioweb-dropzone input{position:absolute;opacity:0;pointer-events:none}.orarioweb-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:24px}.orarioweb-analysis-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px}.orarioweb-slot-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 18px}.orarioweb-slot{display:grid;grid-template-columns:130px 1fr 18px 1fr;gap:7px;align-items:center}.orarioweb-title-row{display:flex;justify-content:space-between;gap:20px}.orarioweb-map-table td{vertical-align:middle}.orarioweb-map-table td:first-child{min-width:190px}.orarioweb-map-table small{display:block;color:#047857}.orarioweb-tutor{max-width:520px}.orarioweb-mode-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:18px 0}.orarioweb-mode-grid label{display:flex;flex-direction:column;gap:6px;border:2px solid #cbd5e1;border-radius:8px;padding:16px;cursor:pointer}.orarioweb-mode-grid label.selected{border-color:#0284c7;background:#f0f9ff}.orarioweb-mode-grid label.replace.selected{border-color:#dc2626;background:#fef2f2}.orarioweb-mode-grid label.disabled{opacity:.5;cursor:not-allowed}.orarioweb-summary{display:flex;flex-wrap:wrap;gap:10px;margin:18px 0}.orarioweb-summary span{padding:9px 12px;border-radius:6px;background:#f1f5f9}.orarioweb-summary .ok{background:#dcfce7}.orarioweb-summary .warning{background:#fef3c7}.orarioweb-summary .error{background:#fee2e2}.orarioweb-preview-list{max-height:330px;overflow:auto;border:1px solid #dbe3eb}.orarioweb-preview-row{display:grid;grid-template-columns:170px 1fr 1fr;gap:8px;padding:8px 10px;border-bottom:1px solid #e2e8f0}.orarioweb-preview-row.error{background:#fef2f2}.orarioweb-preview-row.warning{background:#fffbeb}.orarioweb-preview-row small{grid-column:1/-1;color:#b91c1c}.orarioweb-confirmations label{display:block;margin-top:9px}.orarioweb-loading{padding:11px 30px;background:#e0f2fe;color:#075985}.orarioweb-loading i{display:inline-block;animation:orarioweb-spin 1s linear infinite}.orarioweb-error{margin:14px 30px 0}.orarioweb-result{text-align:center;padding:55px}.orarioweb-result>i{font-size:64px;color:#10b981}.orarioweb-result .orarioweb-actions{justify-content:center}@keyframes orarioweb-spin{to{transform:rotate(360deg)}}@media(max-width:960px){.orarioweb-guide-grid{grid-template-columns:1fr 1fr}}@media(max-width:760px){.orarioweb-steps{grid-template-columns:1fr}.orarioweb-analysis-grid,.orarioweb-slot-grid,.orarioweb-mode-grid,.orarioweb-guide-grid,.orarioweb-guide-modes{grid-template-columns:1fr}.orarioweb-slot{grid-template-columns:100px 1fr 12px 1fr}.orarioweb-preview-row{grid-template-columns:1fr}.orarioweb-preview-row small{grid-column:auto}}
</style>
